import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  LinearProgress,
  TablePagination,
} from '@mui/material';
import { Block, ArrowBack, Refresh, Person } from '@mui/icons-material';
import { getBlacklistedBorrowers, removeBorrowerBlacklist } from '../../services/request/Borrowers';
import { BorrowerAPIItem } from '../../models/LendingModels';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PaginationConst } from '../../services/constants/AppConstant';

const BlacklistedCustomers: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BorrowerAPIItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PaginationConst.PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBlacklistedBorrowers(page, rowsPerPage);
      if (res?.result) {
        setRows(res.result);
        setTotal(res.totalCount ?? res.result.length);
      } else if (Array.isArray(res)) {
        setRows(res);
        setTotal(res.length);
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load blacklisted customers');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    try {
      const res = await removeBorrowerBlacklist(id);
      const ok = res?.statusCode === 200;
      if (ok) {
        toast.success(res?.message || 'Blacklist removed');
        await load();
      } else {
        toast.error(res?.message || 'Could not remove blacklist');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Request failed';
      toast.error(msg);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/customers')}>
          All customers
        </Button>
        <Typography variant="h4" component="h1" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Block color="error" />
          Blacklisted customers
        </Typography>
        <Button startIcon={<Refresh />} variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Borrowers listed here were marked during a debit write-off (or manually). Removing blacklist clears the flag;
        credit status may return to GOOD if it was BLOCKED only for blacklist.
      </Alert>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total blacklisted: <strong>{total}</strong>
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Credit status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No blacklisted customers.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="action" />
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => navigate(`/admin/customers/${b.id}/debits`)}
                          >
                            {b.name}
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={b.creditStatus || '—'}
                          color={b.creditStatus === 'BLOCKED' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" noWrap title={b.blacklistReason || ''}>
                          {b.blacklistReason || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{b.contactNumber || b.whatsappNumber || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="primary"
                          disabled={removingId === b.id}
                          onClick={() => handleRemove(b.id)}
                        >
                          Remove blacklist
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default BlacklistedCustomers;
